import { isValidPostalCode } from '@/lib/postal-code';

export interface AddressFormValues {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export type AddressField = keyof AddressFormValues;
export type AddressFieldErrors = Partial<Record<AddressField, string>>;

export interface AddressLikeForValidation {
  street?: string | null;
  street1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  postalCode?: string | null;
}

const STATE_OR_PROVINCE_CODE = /^[A-Za-z]{2}$/;
const ADDRESS_FIELD_ORDER: AddressField[] = ['street', 'city', 'state', 'zip'];

export function addressFormValues(address: AddressLikeForValidation): AddressFormValues {
  return {
    street: address.street ?? address.street1 ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.zip ?? address.postalCode ?? '',
  };
}

export function validateAddressFields(values: AddressFormValues): AddressFieldErrors {
  const street = values.street.trim();
  const city = values.city.trim();
  const state = values.state.trim();
  const zip = values.zip.trim();
  const errors: AddressFieldErrors = {};

  if (!street) errors.street = 'Street is required.';
  if (!city) errors.city = 'City is required.';
  if (!state) {
    errors.state = 'State or province is required.';
  } else if (!STATE_OR_PROVINCE_CODE.test(state)) {
    errors.state = 'Use a two-letter state or province code.';
  }
  if (!zip) {
    errors.zip = 'ZIP or postal code is required.';
  } else if (!isValidPostalCode(zip)) {
    errors.zip = 'Enter a valid US ZIP or Canadian postal code.';
  }

  return errors;
}

/** What a manual-entry form holds: the validated fields plus the optional
 *  secondary line. Nothing requires `street2`, so nothing validates it — but it
 *  is still address input the user typed. */
export interface ManualAddressFormValues extends AddressFormValues {
  street2?: string;
}

/** Nothing typed at all — the payload omits a manual address in this state, so
 *  there is nothing for the server to reject and nothing to validate. `street2`
 *  counts here even though it is never required: an apartment line on its own is
 *  not an address the payload will keep, so skipping validation on it would let
 *  a save silently discard what the user typed. */
export function isEmptyAddress(values: ManualAddressFormValues): boolean {
  return !values.street2?.trim() && ADDRESS_FIELD_ORDER.every((field) => !values[field].trim());
}

export function hasAddressErrors(errors: AddressFieldErrors): boolean {
  return ADDRESS_FIELD_ORDER.some((field) => Boolean(errors[field]));
}

export function firstAddressError(label: string, errors: AddressFieldErrors): string | null {
  const field = ADDRESS_FIELD_ORDER.find((candidate) => errors[candidate]);
  return field ? `${label}: ${errors[field]}` : null;
}
